import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { CheckCheck, Loader2, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal showing the historical (anonymized) CRs that back a Smart Defaults
 * suggestion. Surfaces peer consultants' actual framing so users can see
 * "how my peers pitched this removal" at a glance.
 */
export function PatternDetailsDialog({ open, onOpenChange, componentName, action = 'remove', decision = 'accepted' }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !componentName) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/scope-pack/change-patterns/details`, {
      params: { component: componentName, action, decision, limit: 25 },
      withCredentials: true,
    }).then(res => { if (!cancelled) setItems(res.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, componentName, action, decision]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="pattern-details-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <CheckCheck className="w-4 h-4 text-emerald-600" />
            How peers have removed <span className="font-normal text-slate-600">&ldquo;{componentName}&rdquo;</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            These are the actual <strong>{decision}</strong> change-request proposals for this component
            (client names anonymized). Use them as a starting point when framing the conversation.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
          </div>
        )}

        {!loading && items && items.length === 0 && (
          <div className="text-xs text-slate-400 italic py-6 text-center">
            No historical proposals found.
          </div>
        )}

        {!loading && items && items.length > 0 && (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.cr_id} className="border border-slate-100 rounded-md p-3 bg-slate-50/40"
                data-testid={`pattern-detail-${it.cr_id}`}>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-2">
                  <Badge variant="outline" className="bg-white text-slate-600 border-slate-200 text-[10px] px-1.5 py-0">
                    {it.client}
                  </Badge>
                  <span>&middot;</span>
                  <span>by {it.consultant_name || '—'}</span>
                  {it.resolved_at && (
                    <>
                      <span>&middot;</span>
                      <span>{new Date(it.resolved_at).toLocaleDateString()}</span>
                    </>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <Quote className="w-3.5 h-3.5 mt-0.5 text-[#4ABFCF] shrink-0" />
                  <div className="text-xs text-slate-800 whitespace-pre-wrap flex-1">
                    {it.proposed_note}
                  </div>
                </div>
                {it.decision_note && (
                  <div className="mt-2 text-[11px] text-slate-500 italic pl-5">
                    Client reply: &ldquo;{it.decision_note}&rdquo;
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
